import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { TagInput } from '../ui/TagInput';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { deleteBlockedReason, treatmentLinks } from './catalogLinks';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { CheckList } from '../ui/CheckList';
import { treatmentSeoChecks } from './catalogSeo';
import { treatmentSeoAuto } from '../../utils/seoAuto';
import { treatmentCategories } from '../../data/treatments';
import { KuurPriceNote } from './KuurPriceNote';
import { MediaField } from '../ui/MediaField';
import { emptyMedia, hasMedia } from '../../core/rules/media';
import { KUUR_DEFAULT_SESSIONS, KUUR_MIN_SESSIONS, TAG_SUGGESTIONS, asTreatmentCategory, emptyTreatment, uniqueSorted } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import type { Treatment } from '../../core/types/catalog';
const back = {
  to: '/be/behandelingen',
  label: 'Alle behandelingen'
};
const EMPTY_MEDIA = emptyMedia();
type Kuur = NonNullable<Treatment['kuur']>;

/** Eén behandeling: tekst, duur, prijs, kuur en kenmerken. Het online consult wordt bij Instellingen beheerd. */
export function TreatmentEditor() {
  const {
    slug = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    site,
    putItem,
    removeItem
  } = useAdmin();
  // Het concept: een wijziging in Instellingen (consult, maximum sessies) werkt hier meteen door.
  const policy = site.policy;
  const {
    isNew,
    item: t,
    set
  } = useEditorItem('treatments', slug, emptyTreatment);
  if (!t) return <MissingItem title="Behandeling niet gevonden" back={back} data-id="element-950" data-mp="13zml4ugzc2jv" />;
  const isConsult = t.slug === policy.consult.slug;
  const linked = treatmentLinks(catalog, t.slug);
  const allFocus = uniqueSorted(catalog.treatments.map(x => x.focus));
  const kuur = t.kuur;
  const setKuur = (patch: Partial<Kuur>) => kuur && set({
    kuur: {
      ...kuur,
      ...patch
    }
  });
  const add = () => {
    const created = {
      ...t,
      slug: uniqueSlug(slugify(t.name), catalog.treatments.map(x => x.slug))
    };
    putItem('treatments', created);
    navigate(`/be/behandelingen/${created.slug}`, {
      replace: true
    });
  };
  return <div data-id="element-951" data-mp="1dx0pvwtjj2wc">
      <PageHeader title={isNew ? 'Nieuwe behandeling' : t.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!t.name.trim()} className={btnPrimary} data-id="element-953" data-mp="18xo1v8qxnps6">
              Behandeling toevoegen
            </button> : <>
              {!isConsult && <DeleteButton undoable blockedReason={deleteBlockedReason(linked, 'Koppel eerst los.')} onConfirm={() => {
        const restore = removeItem('treatments', t.slug);
        navigate(back.to);
        undoToast(`“${t.name || 'Naamloze behandeling'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Behandeling teruggezet'
        });
      }} data-id="element-954" data-mp="3tqhtv8n2xhno" />}
              <ViewOnSite path="/behandelingen" data-id="element-955" data-mp="b4ogu8ppac0i" />
            </>} data-id="element-952" data-mp="1dwr6wt6wxw05" />

      {isConsult && <p className="mb-6 rounded-md border border-line bg-shell px-4 py-3 text-[13px] leading-relaxed text-muted" data-id="element-956" data-mp="g4158xw7zr57">
          Naam, duur en prijs van het online consult stel je in bij{' '}
          <Link to="/be/instellingen?tab=afspraken" className="text-ink underline underline-offset-2" data-id="element-957" data-mp="2hve5h9n8taux">
            Instellingen › Afspraken & kuren
          </Link>
          , omdat ze ook in de FAQ en de voorwaarden staan.
        </p>}

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-958" data-mp="h4lzvzb6oxsp">
        <div className="space-y-6 lg:col-span-8" data-id="element-959" data-mp="c6u7732pt7ng">
          <Panel title="Behandeling" data-id="element-960" data-mp="c6c53ptdfz7m">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-961" data-mp="q6mx4w6f3jud">
              <Field label="Naam" className="sm:col-span-2" data-id="element-962" data-mp="q5bqmoll7xzk">
                <input value={t.name} onChange={e => set({
                name: e.target.value
              })} disabled={isConsult} className={inputCls} data-id="element-963" data-mp="l70kanui45m3" />
              </Field>
              <Field label="Korte zin" hint="Onder de naam in de lijst." className="sm:col-span-2" data-id="element-964" data-mp="3tnlm9koptyfp">
                <input value={t.tagline} onChange={e => set({
                tagline: e.target.value
              })} className={inputCls} data-id="element-965" data-mp="3pnl5vui23d60" />
              </Field>
              <Field label="Omschrijving" className="sm:col-span-2" data-id="element-966" data-mp="2harg6aattwt">
                <textarea value={t.description} onChange={e => set({
                description: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-967" data-mp="2exyky305z4z" />
              </Field>
              <Field label="Categorie" data-id="element-968" data-mp="35xhhvb3pdunn">
                <select value={t.category} onChange={e => {
                const category = asTreatmentCategory(e.target.value);
                if (category) set({
                  category
                });
              }} className={inputCls} data-id="element-969" data-mp="1lfyx1136wgfx">
                  {treatmentCategories.map(c => <option key={c} data-id="element-970" data-mp="1hen5ur4o3n5m">{c}</option>)}
                </select>
              </Field>
              <Field label="Duur (minuten)" hint="Bepaalt ook de tijdsloten in de agenda." data-id="element-971" data-mp="251lg37271yv3">
                <NumberInput value={t.durationMinutes || undefined} onChange={v => set({
                durationMinutes: v ?? 0
              })} min={15} max={480} emptyMessage="Vul de duur in minuten in." disabled={isConsult} data-id="element-972" data-mp="294i2l8uuuf4b" />
              </Field>
              <Field label="Waar het om draait" as="div" className="sm:col-span-2" data-id="element-973" data-mp="opzeqex8m3k5">
                <TagInput label="Waar het om draait" value={t.focus} onChange={focus => set({
                focus
              })} suggestions={allFocus.filter(f => !t.focus.includes(f)).slice(0, TAG_SUGGESTIONS)} data-id="element-974" data-mp="4n3iodm9qxlm" />
              </Field>
            </div>
          </Panel>

          <Panel title="Prijs" data-id="element-975" data-mp="3ryuphu3uej6z">
            <div className="space-y-5" data-id="element-976" data-mp="3ry2uake5qnh8">
              <Toggle checked={t.price === null} onChange={onlyKuur => set({
              price: onlyKuur ? null : 0
            })} label="Alleen als kuur te boeken" description="Dan staat er “Alleen als kuur” in plaats van een losse prijs." data-id="element-977" data-mp="3n24893gaxhjz" />
              {t.price !== null && <Field label="Prijs per behandeling (€)" data-id="element-978" data-mp="391tg7x399wx8">
                  <NumberInput decimals value={t.price || undefined} onChange={v => set({
                price: v ?? 0
              })} emptyMessage="Vul een prijs in, bijv. 95." disabled={isConsult} className="max-w-[12rem]" data-id="element-979" data-mp="3438etuc9p777" />
                </Field>}
              <Toggle checked={Boolean(kuur)} onChange={on => set({
              kuur: on ? {
                sessions: KUUR_DEFAULT_SESSIONS,
                price: 0
              } : undefined
            })} label="Ook als kuur aanbieden" data-id="element-980" data-mp="1etfzdqzp24qv" />
              {kuur && <div className="grid max-w-md gap-4 sm:grid-cols-2" data-id="element-981" data-mp="1ollngpmts20s">
                  <Field label="Aantal behandelingen" data-id="element-982" data-mp="qx241bg28ffl">
                    <NumberInput value={kuur.sessions} onChange={v => v !== undefined && setKuur({
                  sessions: v
                })} min={KUUR_MIN_SESSIONS} max={policy.courseMaxSessions} data-id="element-983" data-mp="vulj2dkl0w8l" />
                  </Field>
                  <Field label="Prijs kuur (€)" data-id="element-984" data-mp="2c9d94ju99req">
                    <NumberInput decimals value={kuur.price || undefined} onChange={v => setKuur({
                  price: v ?? 0
                })} emptyMessage="Vul de kuurprijs in." data-id="element-985" data-mp="2c8ttj9s2vf3v" />
                  </Field>
                  <KuurPriceNote sessions={kuur.sessions} kuurPrice={kuur.price} singlePrice={t.price} data-id="element-986" data-mp="32d0glik7rn5n" />
                </div>}
            </div>
          </Panel>

          {!isConsult && <Panel title="Beeld" description="Staat groot naast de uitleg als iemand de behandeling openklapt op de salonpagina." data-id="element-987" data-mp="3c2tpgbsz1o67">
              <MediaField label="Foto of korte video" value={t.media ?? EMPTY_MEDIA}
          // Helemaal leeg = veld weg (zelfde opslag als vóór beelden); alleen tekst blijft staan tijdens het typen.
          onChange={media => set({
            media: media.alt || media.video || hasMedia(media) ? media : undefined
          })} emptyNote="Leeg: de behandeling staat er zonder beeld, alleen met tekst." data-id="element-988" data-mp="379h54dvbs3j9" />
            </Panel>}

          <SeoPanel seo={t.seo} onChange={seo => set({
          seo
        })} auto={treatmentSeoAuto(t)} path={null} imageFallback="de algemene salonfoto" data-id="element-989" data-mp="20ozhyj87zp23" />
        </div>

        <aside aria-label="Behandelinstellingen" className="space-y-6 lg:col-span-4" data-id="element-990" data-mp="b6ssulugvxi7">
          <Panel title="Kenmerken" data-id="element-991" data-mp="g543gysl6c1h">
            <div className="space-y-1" data-id="element-992" data-mp="g41benjj23jz">
              <Toggle checked={Boolean(t.signature)} onChange={signature => set({
              signature
            })} label="Signature-behandeling" description="Krijgt een label op de salonpagina." data-id="element-993" data-mp="1g7u8yesmsza" />
              <Toggle checked={Boolean(t.bindweefsel)} onChange={bindweefsel => set({
              bindweefsel
            })} label="Met bindweefselmassage" data-id="element-994" data-mp="3ojcp1mwoy2k5" />
              <Toggle checked={Boolean(t.online)} onChange={online => set({
              online
            })} label="Online (videocall)" data-id="element-995" data-mp="3emq30usmp5gj" />
            </div>
          </Panel>
          <Panel title="Wat mist er nog?" data-id="element-996" data-mp="3jm3008d7rswu">
            <CheckList checks={treatmentSeoChecks(t, catalog.treatments)} okText="Deze behandeling is compleet" data-id="element-997" data-mp="1cvd0gce4v9nn" />
          </Panel>
          {linked.length > 0 && <Panel title="Gekoppeld aan" data-id="element-998" data-mp="1htes3lpmkhaq">
              <ul className="space-y-1 text-[13px] text-muted" data-id="element-999" data-mp="21md5f4ygjtia">
                {linked.map(l => <li key={l} data-id="element-1000" data-mp="26e4zhpgtohd6">{l}</li>)}
              </ul>
            </Panel>}
        </aside>
      </div>
    </div>;
}